import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, Users, Clock, ChevronLeft, ChevronRight, 
  Plus, RefreshCw, Briefcase, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SchedulingBoard = () => {
  const [loading, setLoading] = useState(true);
  const [workload, setWorkload] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);

  useEffect(() => {
    loadWorkload();
    loadForecast();
  }, [currentWeekOffset]);

  const loadWorkload = async () => {
    setLoading(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + (currentWeekOffset * 7));
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 13);

      const response = await axios.get(`${API}/scheduling/workload`, {
        params: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }
      });
      setWorkload(response.data);
    } catch (error) {
      console.error('Failed to load workload:', error);
      toast.error('Failed to load workload data');
    } finally {
      setLoading(false);
    }
  };

  const loadForecast = async () => {
    try {
      const response = await axios.get(`${API}/scheduling/capacity-forecast`, {
        params: { weeks: 6 }
      });
      setForecast(response.data);
    } catch (error) {
      console.error('Failed to load forecast:', error);
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization > 100) return 'bg-red-500';
    if (utilization > 80) return 'bg-yellow-500';
    if (utilization > 50) return 'bg-green-500';
    return 'bg-blue-500';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'over_capacity':
        return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Over Capacity</Badge>;
      case 'high':
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">High Load</Badge>;
      default:
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Normal</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="scheduling-board">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
            Scheduling Board
          </h1>
          <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
            Workload Planning & Capacity Management
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeekOffset(prev => prev - 1)}
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeekOffset(0)}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentWeekOffset(prev => prev + 1)}
          >
            <ChevronRight size={16} />
          </Button>
          <Button onClick={loadWorkload} variant="outline" className="ml-4">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {workload && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground">Total Capacity</p>
                  <p className="text-2xl font-bold font-mono">{workload.summary.total_capacity_hours}h</p>
                </div>
                <Clock className="text-muted-foreground" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground">Scheduled</p>
                  <p className="text-2xl font-bold font-mono">{workload.summary.total_assigned_hours}h</p>
                </div>
                <Briefcase className="text-muted-foreground" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground">Available</p>
                  <p className="text-2xl font-bold font-mono text-green-500">{workload.summary.available_hours}h</p>
                </div>
                <Calendar className="text-green-500" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-mono uppercase text-muted-foreground">Utilization</p>
                  <p className="text-2xl font-bold font-mono">{workload.summary.overall_utilization}%</p>
                </div>
                <Users className="text-muted-foreground" size={24} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Capacity Forecast */}
      {forecast && (
        <Card className="border-border mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-bold uppercase tracking-tight">
              Capacity Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {forecast.forecast.map((week, idx) => (
                <div 
                  key={idx}
                  className="p-4 border border-border rounded-sm hover:border-primary/30 transition-colors"
                >
                  <p className="text-xs font-mono text-muted-foreground mb-1">{week.week_label}</p>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-lg font-bold font-mono">{week.utilization}%</p>
                    {getStatusBadge(week.status)}
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 mb-2">
                    <div 
                      className={`h-2 rounded-full ${getUtilizationColor(week.utilization)}`}
                      style={{ width: `${Math.min(week.utilization, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {week.scheduled_hours}h / {week.capacity_hours}h
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workload Matrix */}
      {workload && workload.workload_matrix && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg font-bold uppercase tracking-tight">
              Daily Workload by Worker
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-xs font-mono uppercase tracking-wide text-muted-foreground sticky left-0 bg-background">
                      Worker
                    </th>
                    {Object.values(workload.workload_matrix).map((day, idx) => (
                      <th key={idx} className="text-center py-3 px-2 text-xs font-mono uppercase tracking-wide text-muted-foreground min-w-[100px]">
                        <div>{day.day_name.slice(0, 3)}</div>
                        <div className="text-[10px]">{day.date.split('-').slice(1).join('/')}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {workload.workers.map((worker) => (
                    <tr key={worker.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-3 px-4 font-medium sticky left-0 bg-background">
                        {worker.name}
                      </td>
                      {Object.values(workload.workload_matrix).map((day, idx) => {
                        const workerData = day.workers[worker.id];
                        if (!workerData) return <td key={idx} className="text-center py-3 px-2">-</td>;
                        
                        return (
                          <td key={idx} className="text-center py-3 px-2">
                            <div 
                              className={`mx-auto w-16 p-2 rounded ${
                                workerData.over_capacity ? 'bg-red-500/20 border border-red-500/30' :
                                workerData.utilization > 80 ? 'bg-yellow-500/10' :
                                workerData.utilization > 0 ? 'bg-green-500/10' : 'bg-muted/30'
                              }`}
                            >
                              <p className="text-sm font-mono font-bold">{workerData.assigned_hours}h</p>
                              <p className="text-[10px] text-muted-foreground">{workerData.utilization}%</p>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SchedulingBoard;
